// rayTool.js — shared "Ray" click-click multi-drawing tool for both charts (Dashboard's
// SignalChart.js and the More Info chart in App.js). Interaction is deliberately NOT a
// click-and-hold drag (unlike Trend Line/Fib/Channel's stage 1) — per the founder's ask,
// it's two discrete clicks: the first click sets the anchor, the ray then follows the
// mouse freely (no button held) as a live preview, and the second click commits it.
// Same 2-stage "click, then click again" shape as Channel's stage 2 (offset), just
// applied to BOTH points here instead of one. Rendering-wise, a Ray extends infinitely
// past the second anchor point (in the direction from point1 through point2) instead of
// stopping there, same as before. type="ray" in chart_advanced_drawings.
//
// Includes the xToTime/timeToX extrapolation fix from the start (see fibTool.js's header
// comment for the full story) — moving the cursor past the last/first candle would
// otherwise stop short, same bug already found and fixed in Fib/Channel.

import { useState, useRef, useEffect, useCallback } from "react";
import { fetchAdvancedDrawings, insertAdvancedDrawing, deleteAdvancedDrawing } from "./advancedDrawingsStore";

const TYPE = "ray";

// Time↔pixel helpers that extrapolate linearly from the visible range when the native
// coordinateToTime/timeToCoordinate return null — see fibTool.js/channelTool.js's
// identical helpers for the full rationale. Duplicated rather than shared, matching this
// codebase's existing convention for these small per-tool helpers.
function xToTime(chart, x) {
  const ts = chart.timeScale();
  const t = ts.coordinateToTime(x);
  if (t != null) return t;
  const range = ts.getVisibleRange();
  if (!range) return null;
  const xFrom = ts.timeToCoordinate(range.from);
  const xTo = ts.timeToCoordinate(range.to);
  if (xFrom == null || xTo == null || xFrom === xTo) return null;
  return range.from + ((x - xFrom) / (xTo - xFrom)) * (range.to - range.from);
}
function timeToX(chart, time) {
  const ts = chart.timeScale();
  const x = ts.timeToCoordinate(time);
  if (x != null) return x;
  const range = ts.getVisibleRange();
  if (!range) return null;
  const xFrom = ts.timeToCoordinate(range.from);
  const xTo = ts.timeToCoordinate(range.to);
  if (xFrom == null || xTo == null || range.to === range.from) return null;
  return xFrom + ((time - range.from) / (range.to - range.from)) * (xTo - xFrom);
}

// chartRef / seriesRef: refs to the lightweight-charts chart instance + price series.
// containerRef: ref to the chart's DOM container (for mouse event coordinates + listeners).
// userId/symbol/context: identify which saved rows to read/write (context: "dashboard" | "more_info").
export function useRayTool({ chartRef, seriesRef, containerRef, userId, symbol, context }) {
  const [rayMode, setRayMode] = useState(false);
  const [myRays, setMyRays] = useState([]); // [{id, time1, price1, time2, price2}] — DATA space
  const [phase, setPhase] = useState("idle"); // "idle" (waiting for 1st click) | "placing" (waiting for 2nd)
  const [anchor, setAnchor] = useState(null); // {time1, price1} — set by the 1st click, DATA space
  const [draft, setDraft] = useState(null);   // live preview {time1,price1,time2,price2}, not yet persisted
  const [, setRangeTick] = useState(0);
  const anchorRef = useRef(null); // mirrors anchor — read inside the 2nd click handler to avoid stale closures

  useEffect(() => { anchorRef.current = anchor; }, [anchor]);

  useEffect(() => {
    let cancelled = false;
    setMyRays([]);
    if (!userId || !symbol || !context) return;
    fetchAdvancedDrawings(userId, symbol, context, TYPE)
      .then((rows) => {
        if (cancelled) return;
        setMyRays(rows.map((r) => ({ id: r.id, ...r.points })));
      })
      .catch((e) => console.error("[chart_advanced_drawings-read]", e?.message || e));
    return () => { cancelled = true; };
  }, [userId, symbol, context]);

  const pointFromEvent = useCallback((e) => {
    const container = containerRef.current;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!container || !chart || !series) return null;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const price = series.coordinateToPrice(y);
    const time = xToTime(chart, x);
    if (price == null || time == null) return null;
    return { price, time };
  }, [chartRef, seriesRef, containerRef]);

  // Stage 1 — first click sets the anchor. Uses the chart's native click subscription
  // (same primitive Draw Line uses), not a mousedown/drag — there's nothing to drag yet.
  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!rayMode || phase !== "idle" || !chart || !series || !userId) return;

    const handleClick = (param) => {
      if (!param.point) return;
      const price = series.coordinateToPrice(param.point.y);
      const time = xToTime(chart, param.point.x);
      if (price == null || time == null) return;
      setAnchor({ time1: time, price1: price });
      setPhase("placing");
    };

    chart.subscribeClick(handleClick);
    return () => { try { chart.unsubscribeClick(handleClick); } catch (e) { /* chart may already be torn down */ } };
  }, [rayMode, phase, chartRef, seriesRef, userId, symbol, context]);

  // Stage 2 — mouse movement (no button held) drives a live preview from the anchor to
  // the cursor; a second click commits the ray. Mirrors channelTool.js's stage-2 offset
  // preview pattern (mousemove for preview, chart.subscribeClick to finalize).
  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!rayMode || phase !== "placing" || !chart || !series || !userId) return;

    const handleMouseMove = (e) => {
      const p = pointFromEvent(e);
      if (!p) return;
      const a = anchorRef.current;
      if (!a) return;
      setDraft({ time1: a.time1, price1: a.price1, time2: p.time, price2: p.price });
    };
    const handleClick = (param) => {
      if (!param.point) return;
      const price2 = series.coordinateToPrice(param.point.y);
      const time2 = xToTime(chart, param.point.x);
      if (price2 == null || time2 == null) return;
      const a = anchorRef.current;
      if (!a) { setPhase("idle"); return; }
      // Ignore a same-spot click (degenerate ray) — stay in "placing" so the user can
      // just move the mouse and click again, rather than silently discarding the anchor.
      if (a.time1 === time2 && a.price1 === price2) return;
      const finished = { time1: a.time1, price1: a.price1, time2, price2 };
      insertAdvancedDrawing(userId, symbol, context, TYPE, finished)
        .then((row) => setMyRays((prev) => [...prev, { id: row.id, ...row.points }]))
        .catch((e) => console.error("[chart_advanced_drawings-insert]", e?.message || e));
      setAnchor(null);
      setDraft(null);
      setPhase("idle"); // ready for the next ray — multi-drawing mode, like Trend Line/Draw Line
    };

    window.addEventListener("mousemove", handleMouseMove);
    chart.subscribeClick(handleClick);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      try { chart.unsubscribeClick(handleClick); } catch (e) { /* chart may already be torn down */ }
    };
  }, [rayMode, phase, chartRef, seriesRef, pointFromEvent, userId, symbol, context]);

  // Reset mid-flight state if the tool is disarmed while an anchor is already placed.
  useEffect(() => {
    if (!rayMode) {
      setPhase("idle");
      setAnchor(null);
      setDraft(null);
    }
  }, [rayMode]);

  const deleteRay = useCallback((id) => {
    if (!userId) return;
    deleteAdvancedDrawing(userId, id)
      .then(() => setMyRays((prev) => prev.filter((r) => r.id !== id)))
      .catch((e) => console.error("[chart_advanced_drawings-delete]", e?.message || e));
  }, [userId]);

  // WATCHDOG version (2026-07-09) — re-attaches when the chart instance appears or is
  // recreated, immediate tick on attach. See trendLineTool.js comment for why.
  useEffect(() => {
    if (myRays.length === 0 && !draft) return;
    const handler = () => setRangeTick((t) => t + 1);
    let subscribed = null;
    const ensure = () => {
      const chart = chartRef.current;
      if (chart === subscribed) return;
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
      subscribed = chart || null;
      if (chart) {
        try { chart.timeScale().subscribeVisibleLogicalRangeChange(handler); handler(); } catch (e) { subscribed = null; }
      }
    };
    ensure();
    const watchdog = setInterval(ensure, 400);
    return () => {
      clearInterval(watchdog);
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
    };
  }, [chartRef, myRays.length, draft]);

  // Derived: project each ray's two anchor points to pixel space, recomputed every
  // render. Extension past point2 happens in RayOverlay (pure rendering concern), not
  // here — the projected data is just the two literal anchors.
  const projectedRays = [];
  const chart = chartRef.current;
  const series = seriesRef.current;
  if (chart && series) {
    const project = (pts, id) => {
      const x1 = timeToX(chart, pts.time1);
      const x2 = timeToX(chart, pts.time2);
      const y1 = series.priceToCoordinate(pts.price1);
      const y2 = series.priceToCoordinate(pts.price2);
      if (x1 == null || x2 == null || y1 == null || y2 == null) return null;
      return { id, x1, y1, x2, y2 };
    };
    myRays.forEach((r) => {
      const p = project(r, r.id);
      if (p) projectedRays.push(p);
    });
    if (draft) {
      const p = project(draft, null);
      if (p) projectedRays.push({ ...p, isDraft: true });
    }
  }

  return { rayMode, setRayMode, myRays, deleteRay, projectedRays, phase };
}

// SVG overlay — a line from the first anchor through the second, extended 5000px further
// in the same direction so it always reaches the pane's edge regardless of zoom level;
// the SVG root's default overflow:hidden (matching its 100%x100% sizing over the pane)
// clips it there naturally, so no need to know the pane's exact pixel dimensions. Cyan
// (#22d3ee) — distinct from Trend Line/Draw Line (orange), Channel (indigo), and Fib's
// gold/rainbow. pointerEvents: "none" on the SVG root — see measureTool.js's
// MeasureOverlay comment for why that's critical.
export function RayOverlay({ rays }) {
  if (!rays || rays.length === 0) return null;
  return (
    <svg
      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 5, pointerEvents: "none" }}
    >
      {rays.map((r, i) => {
        const dx = r.x2 - r.x1;
        const dy = r.y2 - r.y1;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const EXTEND = 5000;
        const farX = r.x2 + (dx / len) * EXTEND;
        const farY = r.y2 + (dy / len) * EXTEND;
        return (
          <line
            key={r.id ?? `draft-${i}`}
            x1={r.x1} y1={r.y1} x2={farX} y2={farY}
            stroke="#22d3ee" strokeWidth="1.5" strokeOpacity={r.isDraft ? 0.5 : 1}
          />
        );
      })}
    </svg>
  );
}
